/**
 * Landing — "MEET AT THE SQUARE"
 * A cinematic walk through a fictional historic music-town square.
 * The SS Compass hero at the top is authoritative & untouched. Below the
 * hero, this page is a scroll-driven story with FOUR corner buildings, a
 * heart-of-the-square Radio moment, a "song comes first" brand section,
 * and ONE final CTA cluster — never any account buttons before it.
 *
 * Visual strategy: no real photography, no recognisable real places. The
 * fictional square is built from layered CSS scenography (warm windows,
 * gilded silhouettes, street lamps, tree canopies) + subtle framer-motion
 * parallax + Ken-Burns pans. Respects prefers-reduced-motion.
 */
import { registerHref } from "@/lib/authReturn";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Music, Feather, Building2, ShoppingBag, Wrench,
  Radio as RadioIcon, Sparkles, MoonStar, UserPlus, Briefcase,
} from "lucide-react";
import CompassHero from "@/components/hero/CompassHero";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/lib/seo";

export default function Landing() {
  const missionRef = useRef(null);
  const scrollToLearn = () => missionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  useSeo({
    title: "Songwriter Square — Meet at the Square",
    description:
      "A fictional music town square where songs, songwriters, listeners and industry professionals meet. Walk the four corners, listen at the heart, and remember: the song comes first.",
    url: typeof window !== "undefined" ? window.location.origin + "/" : undefined,
    type: "website",
  });

  return (
    <div className="bg-matte text-white" data-testid="landing-root">
      <CompassHero onScrollToLearn={scrollToLearn} />
      <div ref={missionRef} />

      <Arrival />
      <CornerSection
        id="writers-corner"
        anchor="/songwriters"
        eyebrow="Songwriters"
        title="The Writers' Corner"
        tagline="This is where songs begin."
        body="If you write songs, this corner is yours. Build your catalog, keep your lyrics and splits in one place, shape a profile that carries your work, and step into every other part of the Square from here — on your terms, with the song at the center."
        exploreLabel="Enter the Writers' Corner"
        image="/landing/writers-corner.png"
        imageAlt="A softly lit writing lounge inside a historic brick building — an acoustic guitar leaning by a leather chair, an open lyric notebook and Edison lamp glowing on a wooden desk."
        icon={Feather}
        testId="landing-corner-songwriters"
      />
      <CornerSection
        id="industry-house"
        anchor="/music-industry"
        eyebrow="Music Industry"
        title="The Industry House"
        tagline="This is where songs meet opportunity."
        body="If you sign, publish, produce, supervise or place music, this house is yours. Discover writers you haven't met yet, listen to catalogs built with care, and reach the creators behind the songs — quietly, professionally, and without the noise."
        exploreLabel="Enter the Industry House"
        image="/landing/industry-house.png"
        imageAlt="An elegant listening room at golden hour — studio headphones and stacked vinyl on a walnut table beside tall arched windows overlooking a cobblestone lane."
        icon={Building2}
        reverse
        testId="landing-corner-music-industry"
      />
      <CornerSection
        id="market-row"
        anchor="/merchandising"
        eyebrow="Merchandise"
        title="The Market Row"
        tagline="Take the music with you."
        body="If you make and sell merchandise, this row is yours. Open a storefront on the Square, present your goods with the polish they deserve, and connect what you sell to the songs and songwriters your customers already love."
        exploreLabel="Enter the Market Row"
        image="/landing/market-row.png"
        imageAlt="A row of independent storefronts along a wet cobblestone lane at dusk — vinyl records and folded printed tees glowing in warm shop windows beneath gas lamps."
        icon={ShoppingBag}
        testId="landing-corner-merchandise"
      />
      <CornerSection
        id="craft-house"
        anchor="/services"
        eyebrow="Services"
        title="The Craft House"
        tagline="This is where songs get the support they need."
        body="If you produce, engineer, mix, master, shoot, design, coach, or market, this house is yours. Offer the craft you already do best, meet the creators who need exactly that, and help the songs that come through the Square go further."
        exploreLabel="Enter the Craft House"
        image="/landing/craft-house.png"
        imageAlt="A premium recording studio control room at night — a large analog mixing console glowing with amber VU meters below a curved screen showing a golden waveform."
        icon={Wrench}
        reverse
        testId="landing-corner-services"
      />
      <HeartOfTheSquare />
      <SongFirst />
      <LivingSquare />
      <FinalCTA />
      <PublicFooter />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SECTION 1 — ARRIVAL
// Amber-meter skyline hero (Aug 26, 2026 refresh). The composition —
// L/R VU meter columns flanking the stage, GR/OUT/LIMIT/CLIP/MAIN mixer
// details, and a glowing "equalizer skyline" with a soft floor
// reflection + the SONGWRITER SQUARE eyebrow / MEET AT THE SQUARE
// headline / supporting paragraph — is rendered as a single optimized
// static asset so it stays lightweight and matches the reference at
// pixel level. Only decorative CSS gradients + a subtle parallax add
// depth on top; nothing else in this section is CSS-drawn.
// ═══════════════════════════════════════════════════════════════════
function Arrival() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Subtle parallax so the meters feel alive without per-frame JS.
  const bgY  = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-3%", "5%"]);
  const bgOp = useTransform(scrollYProgress, [0, 0.65], [1, 0.9]);

  return (
    <section
      ref={ref}
      data-testid="landing-arrival"
      className="relative overflow-hidden border-y border-gold/25 bg-black"
    >
      {/* Cinematic amber-meter skyline — the reference composition, baked
          into a single optimized asset. Headline + supporting copy are
          part of the artwork so kerning + light bloom stay pixel-perfect. */}
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOp }}
        className="relative w-full will-change-transform"
      >
        <img
          src="/landing/meet-at-the-square-hero.jpg"
          alt="Meet at the Square — Songwriter Square"
          draggable={false}
          className="block w-full h-auto select-none pointer-events-none"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          data-testid="landing-arrival-hero-image"
        />
        {/* Bottom fade so the section blends into the next block below.
            Kept inside the parallax wrapper so it moves with the artwork. */}
        <div className="absolute inset-x-0 bottom-0 h-24 md:h-32 bg-gradient-to-b from-transparent to-matte pointer-events-none" />
      </motion.div>

      {/* Hidden accessible headline for SEO + screen readers so the
          section still exposes real text semantics even though the visual
          copy is baked into the image. */}
      <h1 className="sr-only">Meet at the Square — Songwriter Square. Where songwriters, producers, and artists connect in a premium space built for collaboration, discovery, and the next great record.</h1>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SECTIONS 2–5 — FOUR CINEMATIC CORNERS (real premium imagery)
// ═══════════════════════════════════════════════════════════════════
function CornerSection({ id, anchor, eyebrow, title, tagline, body, exploreLabel, image, imageAlt, icon: Icon, reverse, testId }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Slow cinematic Ken-Burns pan + zoom tied to scroll position.
  const kbScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.08, 1.18]);
  const kbY     = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [-14, 14]);
  const revealY  = useTransform(scrollYProgress, [0, 0.35], [24, 0]);
  const revealOp = useTransform(scrollYProgress, [0, 0.35], [0, 1]);

  return (
    <section
      id={id}
      ref={ref}
      data-testid={testId}
      className="relative overflow-hidden border-b border-white/5"
    >
      <div className={`max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32 grid gap-10 md:gap-16 items-center ${
        reverse ? "md:grid-cols-[1fr_1.15fr]" : "md:grid-cols-[1.15fr_1fr]"
      }`}>
        {/* Cinematic image panel */}
        <div
          className={`relative aspect-[4/3] border border-gold/25 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)] ${
            reverse ? "md:order-2" : "md:order-1"
          }`}
          data-testid={`${testId}-scene`}
        >
          <motion.img
            src={image}
            alt={imageAlt}
            loading="lazy"
            style={{ scale: kbScale, y: kbY }}
            className="absolute inset-0 h-full w-full object-cover will-change-transform select-none"
            draggable={false}
          />
          {/* Subtle vignette + bottom gradient to keep the caption legible */}
          <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_45%,transparent_45%,rgba(0,0,0,0.55)_100%)] pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />
          {/* Gold hairline frame */}
          <div className="absolute inset-2 border border-gold/15 pointer-events-none" />
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-gold/80">
            <Icon className="h-3 w-3" /> {title}
          </div>
        </div>

        {/* Copy */}
        <motion.div style={{ opacity: revealOp, y: revealY }}
          className={`${reverse ? "md:order-1" : "md:order-2"}`}>
          <div className="text-[10px] uppercase tracking-[0.4em] text-gold font-display">{eyebrow}</div>
          <h2 className="mt-3 font-display uppercase tracking-tight text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            <span className="bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">{title}</span>
          </h2>
          <p className="mt-4 text-gold/90 font-display uppercase tracking-[0.25em] text-xs">{tagline}</p>
          <p className="mt-6 text-white/75 text-[15px] leading-relaxed max-w-xl">{body}</p>
          <div className="mt-8">
            <Link
              to={anchor}
              data-testid={`${testId}-explore`}
              className="group inline-flex items-center gap-2 text-gold font-display uppercase tracking-[0.35em] text-xs hover:text-amber-200 transition-colors"
            >
              {exploreLabel}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SECTION 6 — HEART OF THE SQUARE (Radio)
// ═══════════════════════════════════════════════════════════════════
function HeartOfTheSquare() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const spin = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [-20, 380]);

  return (
    <section
      ref={ref}
      id="heart-of-the-square"
      data-testid="landing-heart"
      className="relative overflow-hidden border-b border-gold/20 bg-gradient-to-b from-matte via-black to-matte"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.16),transparent_60%)]" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6 py-28 md:py-40 text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-gold font-display">The Heart of the Square</div>
        <h2 className="mt-4 font-display uppercase tracking-tight text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95]">
          <span className="bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">Songwriter Square Radio</span>
        </h2>
        <p className="mt-6 max-w-3xl mx-auto text-white/75 text-lg leading-relaxed">
          The center of the compass. A place where songs from the Writers&apos; Corner meet the ears of listeners,
          the attention of the Industry House, and the craft of the studios on the south side. Everything the Square
          builds eventually plays here.
        </p>

        {/* Rotating vinyl artwork */}
        <div className="relative mx-auto mt-16 h-48 w-48 md:h-64 md:w-64">
          <motion.div
            style={{ rotate: spin }}
            className="absolute inset-0 rounded-full border-2 border-gold/30 bg-gradient-to-br from-black to-neutral-900 shadow-[0_0_60px_rgba(212,175,55,0.35)]"
            aria-hidden
          >
            {[0.14, 0.32, 0.5, 0.68, 0.86].map((r) => (
              <div key={r} className="absolute inset-0 rounded-full border border-white/5"
                style={{ transform: `scale(${r})` }} />
            ))}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-goldSheen text-black flex items-center justify-center font-display font-black tracking-widest text-lg shadow-goldGlow">
                SS
              </div>
            </div>
          </motion.div>
        </div>

        <Link
          to="/radio"
          data-testid="landing-heart-enter-radio"
          className="group inline-flex items-center gap-2 mt-12 text-gold font-display uppercase tracking-[0.35em] text-xs hover:text-amber-200 transition-colors"
        >
          Enter Songwriter Square Radio
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SECTION 7 — THE SONG COMES FIRST
// ═══════════════════════════════════════════════════════════════════
function SongFirst() {
  return (
    <section data-testid="landing-song-first" className="relative overflow-hidden border-b border-white/5">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#050505_0%,#0a0705_50%,#050505_100%)]" aria-hidden />
      <div className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(212,175,55,0.14),transparent_70%)]" aria-hidden />

      <div className="relative max-w-4xl mx-auto px-6 py-28 md:py-36 text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-gold font-display">
          <Sparkles className="h-3 w-3" /> Our North Star
        </div>
        <h2 className="mt-6 font-display uppercase tracking-tight text-white text-5xl md:text-7xl leading-[0.95]">
          <span className="bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">The Song Comes First.</span>
        </h2>
        <div className="mx-auto mt-6 h-px w-24 gold-divider" />
        <p className="mt-8 text-white/80 text-lg md:text-xl leading-relaxed">
          Songwriter Square is built around the song — not follower counts, not popularity contests,
          not algorithms tuned only to personalities. The song is the center of the ecosystem.
          Writers keep their catalog, their lyrics, their splits and their story — and choose where those songs travel.
        </p>
        <p className="mt-6 text-white/50 text-sm max-w-2xl mx-auto">
          Discovery, opportunity, listening and craft rotate around the writer. The Square is a stage; the song is the star.
        </p>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SECTION 8 — LIVING SQUARE TRANSITION
// ═══════════════════════════════════════════════════════════════════
function LivingSquare() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const kx = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [-30, 30]);
  const ks = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.02, 1.15]);

  return (
    <section ref={ref} data-testid="landing-living-square" className="relative overflow-hidden border-b border-gold/20">
      <div className="absolute inset-0" aria-hidden>
        <motion.div style={{ x: kx, scale: ks }} className="absolute inset-0">
          <TownSquareScene variant="living" reduce={reduce} />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-matte via-transparent to-matte" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 py-32 md:py-44 text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-gold font-display">Evening Falls</div>
        <h2 className="mt-4 font-display uppercase tracking-tight text-white text-4xl md:text-6xl leading-[0.95]">
          <span className="bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">The Square Comes Alive</span>
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-white/70 text-lg leading-relaxed">
          Lamps warm the paving stones. A guitar case snaps shut in the Writers&apos; Corner. A producer answers a demo
          from the Industry House. Vinyl slides into a sleeve at Market Row. A mix bounces at the Craft House.
          And on the record at the heart of the Square, a new song begins to spin.
        </p>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FINAL — MEET AT THE SQUARE (three account CTAs, only here)
// ═══════════════════════════════════════════════════════════════════
function FinalCTA() {
  return (
    <section id="join" data-testid="landing-final-cta" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-matte to-matte" aria-hidden />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.22),transparent_65%)]" aria-hidden />
      <div className="relative max-w-4xl mx-auto px-6 py-28 md:py-40 text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-gold font-display">Come In</div>
        <h2 className="mt-6 font-display uppercase tracking-tight text-white text-5xl md:text-7xl leading-[0.95]">
          <span className="bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">Meet at the Square</span>
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-white/75 text-lg leading-relaxed">
          Explore freely, join as a songwriter, or enter as a business or industry professional.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <Link to="/guest/profile" data-testid="landing-final-guest">
            <Button variant="outline"
              className="rounded-none h-12 w-full sm:w-auto px-8 bg-transparent border-white/25 text-white/85 hover:border-gold hover:text-gold font-display uppercase tracking-widest">
              Browse as Guest
            </Button>
          </Link>
          <Link to={registerHref("/")} data-testid="landing-final-create-account">
            <Button
              className="rounded-none h-12 w-full sm:w-auto px-8 bg-goldSheen text-black font-display uppercase tracking-widest hover:brightness-110 shadow-goldSoft hover:shadow-goldGlow border border-gold/60">
              <UserPlus className="h-4 w-4 mr-2" /> Create Account
            </Button>
          </Link>
          <Link to="/register?type=business" data-testid="landing-final-create-business">
            <Button variant="outline"
              className="rounded-none h-12 w-full sm:w-auto px-8 bg-transparent border-gold/50 text-gold hover:bg-gold/10 hover:border-gold font-display uppercase tracking-widest">
              <Briefcase className="h-4 w-4 mr-2" /> Create Business Account
            </Button>
          </Link>
        </div>

        <p className="mt-8 text-[10px] uppercase tracking-[0.35em] text-white/40">
          Creator Membership · $10 / month · Test mode — no payments captured yet
        </p>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SCENOGRAPHY — pure CSS/SVG town-square scenes (no real photography)
// ═══════════════════════════════════════════════════════════════════

/** Wide establishing / living view of the fictional Square. */
function TownSquareScene({ variant, reduce }) {
  // Warm golden-hour gradient sky + distant façade silhouettes + street lamps.
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background: variant === "living"
            ? "radial-gradient(60% 60% at 50% 30%, #1a0f06 0%, #0a0603 50%, #050303 100%)"
            : "radial-gradient(70% 65% at 50% 25%, #1c1408 0%, #0d0805 55%, #050303 100%)",
        }}
      />
      {/* Distant tree canopy silhouettes */}
      <svg className="absolute inset-x-0 bottom-1/3 w-full h-1/3" viewBox="0 0 800 200" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="canopy" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#0a0603" stopOpacity="0" />
            <stop offset="100%" stopColor="#050303" stopOpacity="1" />
          </linearGradient>
        </defs>
        {[40, 130, 230, 320, 420, 520, 620, 720].map((cx, i) => (
          <ellipse key={i} cx={cx} cy={150 + (i % 3) * 6} rx={70 + (i % 3) * 12} ry={40 + (i % 2) * 10}
            fill="#050303" opacity={0.85} />
        ))}
        <rect x="0" y="140" width="800" height="60" fill="url(#canopy)" />
      </svg>
      {/* Fictional façade silhouettes with warm windows */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 flex items-end justify-center gap-[2px] px-2">
        {[0.55, 0.72, 0.6, 0.85, 0.5, 0.78, 0.65, 0.9, 0.58, 0.7, 0.62, 0.82].map((h, i) => (
          <Facade key={i} height={h} seed={i} reduce={reduce} />
        ))}
      </div>
      {/* Foreground paving glow */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#1a0e05] to-transparent" aria-hidden />
      {/* Street lamps */}
      <div className="absolute inset-x-0 bottom-8 flex justify-between px-8 md:px-16" aria-hidden>
        {[0, 1, 2, 3].map((i) => <Streetlamp key={i} />)}
      </div>
      {/* Ambient drifting sparks (music-in-the-air) */}
      {!reduce && <MusicMotes />}
    </div>
  );
}

/** Individual fictional building façade with softly flickering windows. */
function Facade({ height, seed, reduce }) {
  const cols = 3 + (seed % 3);
  const rows = 3 + (seed % 4);
  return (
    <div className="relative flex-1 max-w-[9%]" style={{ height: `${height * 100}%` }}>
      <div className="absolute inset-0 bg-[#0b0805] border-t border-l border-r border-black/60"
        style={{ boxShadow: "inset 0 -20px 40px rgba(0,0,0,0.6)" }} />
      {/* Rooftop / cornice */}
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-b from-black/80 to-transparent" />
      {/* Window grid */}
      <div className="absolute inset-3 grid gap-[3px]"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
        {Array.from({ length: cols * rows }).map((_, i) => {
          const lit = (seed * 7 + i * 3) % 5 < 3;
          const delay = ((seed + i) % 7) * 0.5;
          return (
            <motion.div
              key={i}
              className="rounded-[1px]"
              initial={{ opacity: lit ? 0.85 : 0.08 }}
              animate={reduce ? {} : (lit ? { opacity: [0.75, 0.95, 0.8] } : {})}
              transition={reduce ? {} : { duration: 4 + delay, repeat: Infinity, repeatType: "mirror" }}
              style={{
                background: lit
                  ? "linear-gradient(180deg, rgba(255,190,110,0.85), rgba(212,140,50,0.55))"
                  : "rgba(255,255,255,0.03)",
                boxShadow: lit ? "0 0 6px rgba(255,180,90,0.45)" : "none",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function Streetlamp() {
  return (
    <div className="relative w-1 h-24 md:h-28 bg-gradient-to-b from-neutral-800 to-neutral-900">
      <div className="absolute -top-2 -left-2 h-3 w-5 border border-neutral-800 bg-neutral-900" />
      <div className="absolute -top-4 -left-6 h-8 w-12 rounded-full bg-[radial-gradient(circle,rgba(255,190,100,0.55),transparent_70%)] pointer-events-none" />
    </div>
  );
}

function MusicMotes() {
  const motes = Array.from({ length: 12 });
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {motes.map((_, i) => {
        const left = (i * 83) % 100;
        const size = 2 + (i % 3);
        const dur = 10 + (i % 5) * 2;
        return (
          <motion.span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              bottom: "10%",
              width: size, height: size,
              background: "rgba(255,200,120,0.7)",
              filter: "blur(1px)",
            }}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 1, 0], y: [-10, -220] }}
            transition={{ duration: dur, repeat: Infinity, delay: i * 0.6, ease: "linear" }}
          />
        );
      })}
    </div>
  );
}
